/*******************************************************************************
    plik  : frontend/src/components/FastCopyProgress.tsx
    autor : Michal Kolodziejski (2:480/112.10)
    data  : 2025.01.06
    copyright  : PTODT <https://ptodt.org.pl>

    opis  : FastCopy-style progress visualization
            Shows two progress bars for even/odd tracks (or sides)
            with track number indicators

    licencja : MIT
*******************************************************************************/

import React, { useMemo } from 'react';
import type { TrackProgress } from '../types';

/**
 * @brief Props for FastCopyProgress component
 */
interface FastCopyProgressProps {
    /** @brief Array of track progress data */
    trackProgress: TrackProgress[];
    /** @brief Current track being processed */
    currentTrack: number;
    /** @brief Current side being processed */
    currentSide: number;
    /** @brief Total number of tracks */
    totalTracks: number;
    /** @brief Number of sides (1 or 2) */
    sides: number;
    /** @brief Total sectors processed */
    processedSectors: number;
    /** @brief Total sectors to process */
    totalSectors: number;
}

/**
 * @brief Single track segment in the progress bar
 */
interface TrackSegmentProps {
    /** @brief Track number */
    trackNumber: number;
    /** @brief Whether track is completed */
    isCompleted: boolean;
    /** @brief Whether track is currently being processed */
    isActive: boolean;
    /** @brief Whether the track data was compressed */
    isCompressed: boolean;
}

/**
 * @brief Individual track segment component
 * @param props Segment properties
 * @return Track segment JSX element
 */
const TrackSegment: React.FC<TrackSegmentProps> = ({
    trackNumber,
    isCompleted,
    isActive,
    isCompressed,
}) => {
    const className = useMemo(() => {
        let base = 'track-segment';
        if (isCompleted) base += ' track-segment--completed';
        if (isActive) base += ' track-segment--active';
        if (isCompressed) base += ' track-segment--compressed';
        return base;
    }, [isCompleted, isActive, isCompressed]);

    return (
        <div
            className={className}
            title={`Track ${trackNumber}${isCompressed ? ' (skompresowany)' : ''}`}
            role="img"
            aria-label={`Track ${trackNumber}: ${isCompleted ? 'ukończony' : isActive ? 'w trakcie' : 'oczekujący'}`}
        />
    );
};

/**
 * @brief Track number label component
 */
interface TrackLabelProps {
    /** @brief Track number to display */
    trackNumber: number;
    /** @brief Total number of tracks (for spacing calculation) */
    totalTracks: number;
}

/**
 * @brief Track number label displayed below progress bar
 * @param props Label properties
 * @return Track label JSX element
 */
const TrackLabel: React.FC<TrackLabelProps> = ({ trackNumber, totalTracks }) => {
    /** @brief Show labels every 20 tracks for 80 tracks, every 10 for smaller disks */
    const interval = totalTracks > 50 ? 20 : 10;
    if (trackNumber % interval !== 0) {
        return null;
    }

    return (
        <span className="track-label">
            {trackNumber}
        </span>
    );
};

/**
 * @brief FastCopy-style progress visualization component
 * @param props Component properties
 * @return Progress visualization JSX element
 */
const FastCopyProgress: React.FC<FastCopyProgressProps> = ({
    trackProgress,
    currentTrack,
    currentSide,
    totalTracks,
    sides,
    processedSectors,
    totalSectors,
}) => {
    /**
     * @brief Calculates overall progress percentage
     */
    const progressPercent = useMemo(() => {
        if (totalSectors === 0) return 0;
        return Math.round((processedSectors / totalSectors) * 100);
    }, [processedSectors, totalSectors]);

    /**
     * @brief Groups track progress by side
     */
    const progressBySide = useMemo(() => {
        const side0: TrackProgress[] = [];
        const side1: TrackProgress[] = [];

        for (const tp of trackProgress) {
            if (tp.side === 0) {
                side0.push(tp);
            } else {
                side1.push(tp);
            }
        }

        return { side0, side1 };
    }, [trackProgress]);

    /**
     * @brief Checks if a track is completed
     */
    const isTrackCompleted = (tp: TrackProgress): boolean => {
        return tp.sectorsCompleted >= tp.totalSectors;
    };

    /**
     * @brief Checks if a track is currently being processed
     */
    const isTrackActive = (tp: TrackProgress): boolean => {
        return tp.trackNumber === currentTrack && tp.side === currentSide;
    };

    /**
     * @brief Generates track numbers array
     */
    const trackNumbers = useMemo(() => {
        const numbers: number[] = [];
        for (let i = 0; i < totalTracks; i++) {
            numbers.push(i);
        }
        return numbers;
    }, [totalTracks]);

    /** @brief If only one side, show simple progress bar */
    if (sides === 1) {
        return (
            <div className="fastcopy-progress" role="region" aria-label="Postęp konwersji">
                <div className="progress-header">
                    <span className="progress-header__label">Postęp:</span>
                    <span className="progress-header__value">{progressPercent}%</span>
                    <span className="progress-header__track">
                        Track {currentTrack}
                    </span>
                </div>

                <div className="progress-bar-container">
                    <div className="progress-bar-wrapper">
                        <div className="progress-bar" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100}>
                            {progressBySide.side0.map((tp) => (
                                <TrackSegment
                                    key={`t${tp.trackNumber}-s${tp.side}`}
                                    trackNumber={tp.trackNumber}
                                    isCompleted={isTrackCompleted(tp)}
                                    isActive={isTrackActive(tp)}
                                    isCompressed={tp.isCompressed}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="track-labels">
                        {trackNumbers.map((num) => (
                            <TrackLabel key={num} trackNumber={num} totalTracks={totalTracks} />
                        ))}
                    </div>
                </div>

                <div className="progress-stats">
                    <span>{processedSectors} / {totalSectors} sektorów</span>
                </div>
            </div>
        );
    }

    /** @brief Two-sided disk - show FastCopy style with two bars */
    return (
        <div className="fastcopy-progress fastcopy-progress--dual" role="region" aria-label="Postęp konwersji">
            <div className="progress-header">
                <span className="progress-header__label">Postęp:</span>
                <span className="progress-header__value">{progressPercent}%</span>
                <span className="progress-header__track">
                    Track {currentTrack}, Strona {currentSide}
                </span>
            </div>

            <div className="progress-dual-container">
                {/* @brief Upper bar - Side 0 (even sectors in FastCopy terms) */}
                <div className="progress-side">
                    <span className="progress-side__label" aria-hidden="true">Strona 0</span>
                    <div className="progress-bar-wrapper">
                        <div
                            className="progress-bar progress-bar--side0"
                            role="progressbar"
                            aria-label="Postęp strony 0"
                        >
                            {progressBySide.side0.map((tp) => (
                                <TrackSegment
                                    key={`t${tp.trackNumber}-s${tp.side}`}
                                    trackNumber={tp.trackNumber}
                                    isCompleted={isTrackCompleted(tp)}
                                    isActive={isTrackActive(tp)}
                                    isCompressed={tp.isCompressed}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* @brief Lower bar - Side 1 (odd sectors in FastCopy terms) */}
                <div className="progress-side">
                    <span className="progress-side__label" aria-hidden="true">Strona 1</span>
                    <div className="progress-bar-wrapper">
                        <div
                            className="progress-bar progress-bar--side1"
                            role="progressbar"
                            aria-label="Postęp strony 1"
                        >
                            {progressBySide.side1.map((tp) => (
                                <TrackSegment
                                    key={`t${tp.trackNumber}-s${tp.side}`}
                                    trackNumber={tp.trackNumber}
                                    isCompleted={isTrackCompleted(tp)}
                                    isActive={isTrackActive(tp)}
                                    isCompressed={tp.isCompressed}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* @brief Track numbers */}
                <div className="track-labels track-labels--dual">
                    {trackNumbers.map((num) => (
                        <TrackLabel key={num} trackNumber={num} totalTracks={totalTracks} />
                    ))}
                </div>
            </div>

            <div className="progress-stats">
                <span className="progress-stats__sectors">
                    {processedSectors} / {totalSectors} sektorów
                </span>
                <span className="progress-stats__info">
                    {totalTracks} ścieżek × {sides} strony
                </span>
            </div>

            {/* @brief Legend */}
            <div className="progress-legend" aria-label="Legenda">
                <div className="progress-legend__item">
                    <span className="progress-legend__color progress-legend__color--pending"></span>
                    <span className="progress-legend__text">Oczekujący</span>
                </div>
                <div className="progress-legend__item">
                    <span className="progress-legend__color progress-legend__color--active"></span>
                    <span className="progress-legend__text">Aktywny</span>
                </div>
                <div className="progress-legend__item">
                    <span className="progress-legend__color progress-legend__color--completed"></span>
                    <span className="progress-legend__text">Ukończony</span>
                </div>
                <div className="progress-legend__item">
                    <span className="progress-legend__color progress-legend__color--compressed"></span>
                    <span className="progress-legend__text">Skompresowany</span>
                </div>
            </div>
        </div>
    );
};

export default FastCopyProgress;
